import React, { useState } from "react";
import { Modal, message, Input } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../Context";
import {
  useQuotationsListQuery,
  useDeleteQuotationMutation,
} from "../../Services";
import { useSystemConfig } from "../../Hook";
import { PageHeader } from "../../components";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY, ROUTE } from "../../Constant";
import { QuotationTable } from "./common/QuotationTable";
import { handlePrint, handleDownload } from "./common/quotationUtils";

export const QuotationsPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    setCart,
    setCustomer,
    setEditingQuotationId,
    setEditingQuotationName,
    setProceedingQuotationId,
    setQuotationTempCustomer,
    clearCart,
  } = useCart();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const {
    data: quotationsList = [],
    isLoading,
    refetch,
  } = useQuotationsListQuery();
  const { storeName, address, phone, currency, receiptFooter } =
    useSystemConfig();
  const deleteMutation = useDeleteQuotationMutation();

  const filteredQuotations = quotationsList.filter((q: any) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      q.quotation_name?.toLowerCase().includes(term) ||
      q.customer_name?.toLowerCase().includes(term) ||
      q.temp_customer_name?.toLowerCase().includes(term) ||
      q.temp_customer_phone?.toLowerCase().includes(term) ||
      q.id.toLowerCase().includes(term)
    );
  });

  const totalQuotations = filteredQuotations.length;
  const paginatedQuotations = filteredQuotations.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const loadQuotationToCart = (q: any, enterEditMode: boolean) => {
    clearCart();

    // Map cart items
    const cartItems = (q.cart_data || []).map((item: any) => ({
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image || "",
      batch_no: item.batch_no || "",
      qty: item.qty,
      cost_price: item.cost_price,
      is_tempory_item: item.is_tempory_item,
      description: item.description || null,
      is_print: item.is_print || false,
      printed_qty: item.printed_qty || 0,
      rawProduct: item.rawProduct || {
        id: item.id,
        title: item.name,
        image_paths: item.image || "",
        stocks: [
          {
            id: 0,
            batch_no: item.batch_no || "",
            retail_price: item.price,
            cost_price: item.cost_price || 0,
            wholesale_price: 0,
            quantity: item.qty,
          },
        ],
      },
    }));

    setCart(cartItems);

    if (q.customer_id) {
      setCustomer({
        id: q.customer_id,
        name: q.customer_name || "",
        credit_limit: q.credit_limit,
      });
      setQuotationTempCustomer(null);
    } else if (q.temp_customer_name) {
      setCustomer(null);
      setQuotationTempCustomer({
        name: q.temp_customer_name,
        phone: q.temp_customer_phone,
        email: q.temp_customer_email,
        address: q.temp_customer_address,
      });
    } else {
      setCustomer(null);
      setQuotationTempCustomer(null);
    }

    if (enterEditMode) {
      setEditingQuotationId(q.id);
      setEditingQuotationName(q.quotation_name);
      setProceedingQuotationId(null);
      message.success("Quotation loaded to cart in EDIT mode.");
    } else {
      setEditingQuotationId(null);
      setEditingQuotationName(null);
      setProceedingQuotationId(q.id);
      message.success("Quotation loaded to cart for CHECKOUT.");
    }
    navigate(`../${ROUTE.sales_billing}`);
  };

  const handleDelete = (q: any) => {
    Modal.confirm({
      title: "Delete Quotation?",
      content: `Are you sure you want to permanently delete quotation "${q.quotation_name}"?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        deleteMutation.mutate(q.id, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Quotation deleted successfully.");
              refetch();
            } else {
              message.error(res?.message || "Failed to delete quotation.");
            }
          },
          onError: () => {
            message.error("Failed to delete quotation.");
          },
        });
      },
    });
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Quotations"
        subText={`Manage saved quotations: ${totalQuotations}`}
        handleButtons={[{ title: "", icon: ReloadOutlined, onClick: refetch }]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Input
              placeholder="Search Name, Customer, Phone..."
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
              allowClear
              onClear={() => {
                setSearchInput("");
                setPage(1);
              }}
              className="w-full sm:w-64"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <QuotationTable
          dataSource={paginatedQuotations}
          loading={isLoading}
          totalQuotations={totalQuotations}
          currentPage={page}
          onPageChange={setPage}
          onProceed={(q) => loadQuotationToCart(q, false)}
          onEdit={(q) => loadQuotationToCart(q, true)}
          onPrint={(q) =>
            handlePrint(q, {
              storeName,
              address,
              phone,
              currency,
              receiptFooter,
            })
          }
          onDownload={(q) =>
            handleDownload(q, {
              storeName,
              address,
              phone,
              currency,
              receiptFooter,
            })
          }
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};
